import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ElementType, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  FiActivity,
  FiArrowUpRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import QuickActionsBar from '../components/QuickActionsBar';
import DashboardSkeleton from './DashboardSkeleton';
import { fetchInitialOveralAnalytics } from '../hooks/analytics';
import { fetchLeads } from '../hooks/leads';
import { fetchEvents } from '../hooks/calendarApi';
import { getTasks } from '../hooks/tasks';
import { FCMTokenGetSend } from '../services/FCMNotificationManager';
import { getCurentOrgDataFromStorage } from '../services/organization';
import {
  AnalyticsOverview,
  CalendarEvent,
  Lead,
  Organization,
  Task,
} from '../utils/types';
import illustrationImage from '../assets/4729679.jpg';

const brand = '#009800ff';
const brandSoft = '#E6FFE6';
const stageColors: Record<string, string> = {
  new: '#2563EB',
  contacted: '#7C3AED',
  qualified: '#0F766E',
  proposal: '#D97706',
  won: '#009800ff',
  lost: '#DC2626',
};
const interestColors = ['#009800ff', '#0F766E', '#2563EB', '#D97706', '#9333EA'];

const formatCompactDate = (value?: string | null) => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatRelativeDay = (value?: string | null) => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (first: Date, second: Date) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();

  if (sameDay(date, today)) return 'Today';
  if (sameDay(date, tomorrow)) return 'Tomorrow';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const getLeadDisplayName = (lead: Lead) =>
  `${lead.first_name || ''} ${lead.second_name || ''}`.trim() || lead.email || lead.phone || `Lead #${lead.id}`;

const getLeadCompanyName = (lead: Lead) => {
  if (!lead.company) return 'No company linked';
  if (typeof lead.company === 'string') return lead.company;
  return lead.company.name || 'No company linked';
};

const getStageLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isTaskOpen = (task: Task) => !['completed', 'cancelled'].includes(task.status);

const isTaskOverdue = (task: Task) => {
  if (!task.due_date || !isTaskOpen(task)) return false;
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

const getLeadResults = (payload: any): Lead[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const DashboardMetricCard = ({
  label,
  value,
  helper,
  icon,
  accent = brand,
}: {
  label: string;
  value: string;
  helper: string;
  icon: ElementType;
  accent?: string;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="22px"
    px={5}
    py={5}
    boxShadow="0 16px 40px rgba(15, 23, 42, 0.05)"
  >
    <HStack justify="space-between" align="flex-start" mb={5}>
      <Box
        boxSize="44px"
        borderRadius="16px"
        bg={brandSoft}
        color={accent}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon as={icon} boxSize={5} />
      </Box>
      <Badge bg={brandSoft} color={brand} borderRadius="full" px={2.5} py={1}>
        Live
      </Badge>
    </HStack>
    <Text fontSize="xs" letterSpacing="0.12em" textTransform="uppercase" fontWeight="800" color="gray.500" mb={2}>
      {label}
    </Text>
    <Text fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1" fontWeight="800" color="gray.900" mb={2}>
      {value}
    </Text>
    <Text fontSize="sm" color="gray.500" lineHeight="1.5">
      {helper}
    </Text>
  </Box>
);

const SectionCard = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius={{ base: '20px', md: '24px' }}
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 18px 40px rgba(15, 23, 42, 0.05)"
  >
    <Stack
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      mb={5}
      spacing={3}
    >
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

const HomePrimaryDashboard = () => {
  const [org, setOrg] = useState<Organization>();
  const [analytics, setAnalytics] = useState<AnalyticsOverview>();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [recentLeads, setRecentLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { orgId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let attempts = 0;
    let active = true;

    const loadOrg = async () => {
      const response = await getCurentOrgDataFromStorage();
      if (!active) return;

      if (response.success && response.data && typeof response.data === 'object') {
        setOrg(response.data);
        return;
      }

      attempts += 1;
      if (attempts < 3) {
        window.setTimeout(loadOrg, 700);
      } else {
        setLoading(false);
      }
    };

    loadOrg();
    return () => {
      active = false;
    };
  }, [orgId]);

  useEffect(() => {
    if (!org?.id) return;

    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const [analyticsResponse, tasksResponse, eventsResponse, leadsResponse] = await Promise.all([
          fetchInitialOveralAnalytics(org.id),
          getTasks(org.id),
          fetchEvents(org.id, { ordering: 'start_datetime' }),
          fetchLeads(org.id, { page_size: '6' }),
        ]);

        if (!active) return;

        if (!analyticsResponse.success) {
          setError('Could not load dashboard analytics.');
        } else {
          setAnalytics(analyticsResponse.data);
        }

        setTasks(Array.isArray(tasksResponse?.data) ? tasksResponse.data : []);
        setEvents(Array.isArray(eventsResponse?.data) ? eventsResponse.data : []);
        setRecentLeads(getLeadResults(leadsResponse?.data).slice(0, 6));
      } catch {
        if (active) {
          setError('Could not load dashboard data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDashboard();
    return () => {
      active = false;
    };
  }, [org]);

  const stageChartData = useMemo(
    () =>
      (analytics?.stage_distribution || []).map((item) => ({
        ...item,
        label: getStageLabel(item.stage),
        fill: stageColors[item.stage] || '#009800ff',
      })),
    [analytics],
  );

  const interestChartData = useMemo(
    () =>
      (analytics?.interest_distribution || []).map((item, index) => ({
        ...item,
        label: getStageLabel(item.level_of_interest),
        fill: interestColors[index % interestColors.length],
      })),
    [analytics],
  );

  const stageBreakdown = useMemo(() => {
    const total = analytics?.total_leads || 0;
    return stageChartData
      .map((item) => ({
        ...item,
        share: total > 0 ? Math.round((item.count / total) * 100) : 0,
      }))
      .sort((first, second) => second.count - first.count);
  }, [analytics, stageChartData]);

  const openTasks = useMemo(() => tasks.filter(isTaskOpen), [tasks]);
  const overdueTasks = useMemo(() => tasks.filter(isTaskOverdue), [tasks]);
  const todayEvents = useMemo(() => {
    const today = new Date();
    return events
      .filter((event) => event.status === 'upcoming')
      .filter((event) => {
        const start = new Date(event.start_datetime);
        return (
          start.getFullYear() === today.getFullYear() &&
          start.getMonth() === today.getMonth() &&
          start.getDate() === today.getDate()
        );
      })
      .slice(0, 4);
  }, [events]);

  const upcomingEvents = useMemo(
    () =>
      events
        .filter((event) => event.status === 'upcoming')
        .sort(
          (first, second) =>
            new Date(first.start_datetime).getTime() -
            new Date(second.start_datetime).getTime(),
        )
        .slice(0, 5),
    [events],
  );

  const leadMomentumDelta =
    (analytics?.new_leads_this_week || 0) - (analytics?.new_leads_last_week || 0);
  const stagePressure = stageBreakdown[0];

  if (loading) {
    return (
      <Box bg="#F6F8FB" minH="100vh" px={{ base: 4, md: 6, xl: 8 }} py={{ base: 4, md: 6 }}>
        <DashboardSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="#F6F8FB" minH="100vh" px={{ base: 4, md: 6, xl: 8 }} py={{ base: 4, md: 6 }}>
        <SectionCard title="Dashboard unavailable" subtitle="The overview could not be loaded with the current data response.">
          <Stack spacing={4}>
            <Text color="red.500">{error}</Text>
            <Button alignSelf="flex-start" bg={brand} color="white" _hover={{ bg: brand }} onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Stack>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box bg="#F6F8FB" minH="100vh" px={{ base: 4, md: 6, xl: 8 }} py={{ base: 4, md: 6 }}>
      <FCMTokenGetSend />

      {!org ? (
        <Flex minH="100vh" align="center" justify="center">
          <Flex
            maxW="1180px"
            w="100%"
            bg="white"
            borderRadius="32px"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="0 30px 70px rgba(15, 23, 42, 0.08)"
            overflow="hidden"
            flexWrap="wrap"
          >
            <Flex flex={{ base: '1 1 100%', lg: '1 1 54%' }} direction="column" justify="center" px={{ base: 6, md: 12 }} py={12}>
              <Badge alignSelf="flex-start" bg={brandSoft} color={brand} borderRadius="full" px={3} py={1.5} mb={5}>
                CRM setup
              </Badge>
              <Text fontSize={{ base: '3xl', md: '4xl' }} fontWeight="800" color="gray.900" mb={4}>
                Create your organization to unlock the command center
              </Text>
              <Text fontSize="lg" color="gray.600" mb={7} maxW="480px" lineHeight="1.7">
                Set up your workspace once and your leads, tasks, team communication, and analytics will start flowing into one place.
              </Text>
              <Button
                size="lg"
                bg={brand}
                color="white"
                width="fit-content"
                rightIcon={<Icon as={FiArrowUpRight as ElementType} />}
                _hover={{ bg: brand }}
                onClick={() => navigate('/new-organization')}
              >
                Create organization
              </Button>
              <Text mt={4} fontSize="sm" color="gray.500">
                The setup only takes a couple of minutes.
              </Text>
            </Flex>

            <Flex flex={{ base: '1 1 100%', lg: '1 1 46%' }} align="center" justify="center" bg="#E6FFE6" position="relative" minH={{ base: '280px', lg: '100%' }}>
              <Image src={illustrationImage} alt="Create organization illustration" maxW="78%" />
              <Box position="absolute" top="-36px" right="-36px" w="120px" h="120px" bg={brand} borderRadius="full" opacity={0.14} />
              <Box position="absolute" bottom="-48px" left="-48px" w="148px" h="148px" bg={brand} borderRadius="full" opacity={0.12} />
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Stack spacing={6}>
          <Box
            bg="linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%)"
            borderWidth="1px"
            borderColor="rgba(148, 163, 184, 0.22)"
            borderRadius={{ base: '26px', md: '32px' }}
            px={{ base: 5, md: 8 }}
            py={{ base: 6, md: 8 }}
            boxShadow="0 24px 80px rgba(15, 23, 42, 0.07)"
            overflow="hidden"
            position="relative"
          >
            <Box
              position="absolute"
              top="-120px"
              left="-80px"
              w="280px"
              h="280px"
              bg="radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, rgba(37, 99, 235, 0) 70%)"
              pointerEvents="none"
            />
            <Box
              position="absolute"
              right="-80px"
              bottom="-120px"
              w="320px"
              h="320px"
              bg="radial-gradient(circle, rgba(0, 152, 0, 0.12) 0%, rgba(0, 152, 0, 0) 72%)"
              pointerEvents="none"
            />
            <Grid templateColumns="1fr" gap={{ base: 6, xl: 8 }} alignItems="stretch" position="relative" zIndex={1}>
              <GridItem>
                <HStack spacing={3} mb={6}>
                  <Avatar bg={brand} color="white" size="sm" name={org.name || 'CRM'} boxShadow="0 10px 24px rgba(0, 152, 0, 0.18)" />
                  <Box>
                    <Text fontSize="xs" fontWeight="800" letterSpacing="0.16em" color="gray.500" textTransform="uppercase">
                      Dashboard Home
                    </Text>
                    <Text fontWeight="700" color="gray.900">
                      {org.name}
                    </Text>
                  </Box>
                </HStack>

                <Stack spacing={5} maxW="720px">
                  <Box>
                    <Text
                      fontSize={{ base: '3xl', md: '4xl' }}
                      lineHeight={{ base: '1.08', md: '1.02' }}
                      fontWeight="900"
                      letterSpacing="-0.04em"
                      color="gray.900"
                    >
                      {org.name}
                    </Text>
                    <Text mt={3} fontSize={{ base: 'sm', md: 'md' }} color="gray.500" maxW="520px" lineHeight="1.7">
                      Your sales pipelines, tasks, and calendar signals in one place.
                    </Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <Box
                      bg="rgba(231, 245, 238, 0.72)"
                      borderWidth="1px"
                      borderColor="rgba(0, 152, 0, 0.10)"
                      borderRadius="20px"
                      px={4}
                      py={4}
                      backdropFilter="blur(12px)"
                    >
                      <Text fontSize="xs" fontWeight="800" letterSpacing="0.14em" textTransform="uppercase" color={brand} mb={2}>
                        Total Leads
                      </Text>
                      <Text fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1" fontWeight="900" letterSpacing="-0.04em" color="gray.900">
                        {analytics?.total_leads || 0}
                      </Text>
                    </Box>
                    <Box
                      bg="rgba(239, 246, 255, 0.9)"
                      borderWidth="1px"
                      borderColor="rgba(29, 78, 216, 0.10)"
                      borderRadius="20px"
                      px={4}
                      py={4}
                      backdropFilter="blur(12px)"
                    >
                      <Text fontSize="xs" fontWeight="800" letterSpacing="0.14em" textTransform="uppercase" color="#1D4ED8" mb={2}>
                        Open Tasks
                      </Text>
                      <Text fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1" fontWeight="900" letterSpacing="-0.04em" color="gray.900">
                        {openTasks.length}
                      </Text>
                    </Box>
                    <Box
                      bg="rgba(245, 243, 255, 0.92)"
                      borderWidth="1px"
                      borderColor="rgba(124, 58, 237, 0.10)"
                      borderRadius="20px"
                      px={4}
                      py={4}
                      backdropFilter="blur(12px)"
                    >
                      <Text fontSize="xs" fontWeight="800" letterSpacing="0.14em" textTransform="uppercase" color="#7C3AED" mb={2}>
                        Upcoming Events
                      </Text>
                      <Text fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1" fontWeight="900" letterSpacing="-0.04em" color="gray.900">
                        {upcomingEvents.length}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Stack>
              </GridItem>
            </Grid>
          </Box>

          <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4}>
            <DashboardMetricCard
              label="Lead Volume"
              value={String(analytics?.total_leads || 0)}
              helper={`${analytics?.new_leads_this_week || 0} this week`}
              icon={FiUsers as ElementType}
            />
            <DashboardMetricCard
              label="Pipeline Conversion"
              value={`${analytics?.conversion_rate_percent || 0}%`}
              helper={`${analytics?.new_leads_last_week || 0} last week`}
              icon={FiTrendingUp as ElementType}
            />
            <DashboardMetricCard
              label="Follow-Up Coverage"
              value={String(analytics?.follow_ups_scheduled || 0)}
              helper={overdueTasks.length > 0 ? `${overdueTasks.length} overdue tasks` : 'No overdue tasks'}
              icon={FiClock as ElementType}
            />
            <DashboardMetricCard
              label="High Intent Leads"
              value={String(analytics?.highly_interested_leads || 0)}
              helper={leadMomentumDelta >= 0 ? `+${leadMomentumDelta} vs last week` : `-${Math.abs(leadMomentumDelta)} vs last week`}
              icon={FiActivity as ElementType}
            />
          </SimpleGrid>

          <QuickActionsBar orgId={org.id} />

          <Grid templateColumns={{ base: '1fr', xl: '1.4fr 0.9fr' }} gap={4}>
            <GridItem>
              <SectionCard
                title="Pipeline composition"
                subtitle="Where leads are concentrated right now."
                action={
                  <Badge bg="#EEF2FF" color="#4338CA" borderRadius="full" px={3} py={1.5}>
                    {stagePressure ? `${stagePressure.label} leads the pipeline` : 'No stage data'}
                  </Badge>
                }
              >
                <Grid templateColumns={{ base: '1fr', lg: '1.2fr 0.8fr' }} gap={4}>
                  <Box h="280px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageChartData} margin={{ top: 10, right: 8, left: -24, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#E5E7EB" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                        <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                        <Tooltip cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }} />
                        <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                          {stageChartData.map((entry) => (
                            <Cell key={entry.stage} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Stack spacing={3}>
                    {stageBreakdown.length > 0 ? (
                      stageBreakdown.map((item) => (
                        <Box key={item.stage} bg="gray.50" borderRadius="16px" px={4} py={3}>
                          <HStack justify="space-between" mb={1}>
                            <HStack spacing={2}>
                              <Box boxSize="10px" borderRadius="full" bg={item.fill} />
                              <Text fontWeight="700" color="gray.900">
                                {item.label}
                              </Text>
                            </HStack>
                            <Text fontWeight="800" color="gray.900">
                              {item.count}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.500">
                            {item.share}% of the current lead base
                          </Text>
                        </Box>
                      ))
                    ) : (
                      <Box bg="gray.50" borderRadius="16px" px={4} py={4}>
                        <Text color="gray.500">Stage distribution will appear as soon as leads are available.</Text>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </SectionCard>
            </GridItem>

            <GridItem>
              <SectionCard
                title="Interest quality"
                subtitle="Interest quality across the pipeline."
              >
                <Box h="280px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={interestChartData}
                        dataKey="count"
                        nameKey="label"
                        innerRadius={56}
                        outerRadius={92}
                        paddingAngle={4}
                      >
                        {interestChartData.map((entry) => (
                          <Cell key={entry.level_of_interest} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Stack spacing={2} mt={2}>
                  {interestChartData.length > 0 ? (
                    interestChartData.map((item) => (
                      <HStack key={item.level_of_interest} justify="space-between">
                        <HStack spacing={2}>
                          <Box boxSize="10px" borderRadius="full" bg={item.fill} />
                          <Text fontSize="sm" color="gray.600">
                            {item.label}
                          </Text>
                        </HStack>
                        <Text fontWeight="700" color="gray.900">
                          {item.count}
                        </Text>
                      </HStack>
                    ))
                  ) : (
                    <Text color="gray.500">Interest-level distribution is not available yet.</Text>
                  )}
                </Stack>
              </SectionCard>
            </GridItem>
          </Grid>

          <Grid templateColumns={{ base: '1fr', xl: '1.1fr 0.9fr' }} gap={4}>
            <GridItem>
              <SectionCard
                title="Lead momentum"
                subtitle="This week versus last week."
                action={
                  <Badge
                    bg={leadMomentumDelta >= 0 ? brandSoft : '#FEF2F2'}
                    color={leadMomentumDelta >= 0 ? brand : '#B91C1C'}
                    borderRadius="full"
                    px={3}
                    py={1.5}
                  >
                    {leadMomentumDelta >= 0 ? '+' : '-'}
                    {Math.abs(leadMomentumDelta)} vs last week
                  </Badge>
                }
              >
                <Box h="260px">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={[
                        { label: 'Last week', leads: analytics?.new_leads_last_week || 0 },
                        { label: 'This week', leads: analytics?.new_leads_this_week || 0 },
                      ]}
                      margin={{ top: 10, right: 10, left: -28, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="leadMomentumFill" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor={brand} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={brand} stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="leads" stroke={brand} strokeWidth={3} fill="url(#leadMomentumFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </SectionCard>
            </GridItem>

            <GridItem>
              <SectionCard
                title="Team ownership"
                subtitle="Current ownership snapshot."
              >
                <Stack spacing={4}>
                  <Box bg="gray.50" borderRadius="18px" px={4} py={4}>
                    <HStack spacing={3}>
                      <Avatar name={analytics?.top_sales_rep?.assigned_to__user__username || 'No owner'} bg={brand} color="white" />
                      <Box>
                        <Text fontWeight="800" color="gray.900">
                          {analytics?.top_sales_rep?.assigned_to__user__username || 'No top rep yet'}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {analytics?.top_sales_rep?.lead_count || 0} leads assigned
                        </Text>
                      </Box>
                    </HStack>
                  </Box>

                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                    <Box bg={brandSoft} borderRadius="16px" px={4} py={4}>
                      <HStack spacing={3} mb={2}>
                        <Icon as={FiCheckCircle as ElementType} color={brand} />
                        <Text fontWeight="700" color="gray.900">
                          Open tasks
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="800" color="gray.900">
                        {openTasks.length}
                      </Text>
                    </Box>
                    <Box bg="#FEFCE8" borderRadius="16px" px={4} py={4}>
                      <HStack spacing={3} mb={2}>
                        <Icon as={FiCalendar as ElementType} color="#A16207" />
                        <Text fontWeight="700" color="gray.900">
                          Upcoming events
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="800" color="gray.900">
                        {upcomingEvents.length}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Stack>
              </SectionCard>
            </GridItem>
          </Grid>

          <Grid templateColumns={{ base: '1fr', xl: '1.1fr 0.9fr' }} gap={4}>
            <GridItem>
              <SectionCard
                title="Work needing attention"
                subtitle="Deadlines and upcoming work."
                action={
                  <Button variant="ghost" color={brand} rightIcon={<Icon as={FiArrowUpRight as ElementType} />} onClick={() => navigate(`/organization/${org.id}/tasks`)}>
                    Open tasks
                  </Button>
                }
              >
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <Box bg="#FEF2F2" borderRadius="18px" px={4} py={4}>
                    <Text fontSize="xs" fontWeight="800" letterSpacing="0.12em" color="#B91C1C" textTransform="uppercase" mb={2}>
                      Overdue
                    </Text>
                    <Text fontSize="3xl" fontWeight="800" color="gray.900">
                      {overdueTasks.length}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Open tasks past due.
                    </Text>
                  </Box>
                  <Box bg="#EFF6FF" borderRadius="18px" px={4} py={4}>
                    <Text fontSize="xs" fontWeight="800" letterSpacing="0.12em" color="#1D4ED8" textTransform="uppercase" mb={2}>
                      Today
                    </Text>
                    <Text fontSize="3xl" fontWeight="800" color="gray.900">
                      {todayEvents.length}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Scheduled for today.
                    </Text>
                  </Box>
                </SimpleGrid>

                <Divider my={5} />

                <Stack spacing={3}>
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map((event) => (
                      <HStack
                        key={event.id}
                        justify="space-between"
                        align="flex-start"
                        bg="gray.50"
                        borderRadius="16px"
                        px={4}
                        py={3}
                      >
                        <Box>
                          <Text fontWeight="700" color="gray.900">
                            {event.title}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {formatRelativeDay(event.start_datetime)} · {formatCompactDate(event.start_datetime)}
                          </Text>
                        </Box>
                        <Badge borderRadius="full" px={2.5} py={1}>
                          {event.event_type.replace(/_/g, ' ')}
                        </Badge>
                      </HStack>
                    ))
                  ) : (
                    <Box bg="gray.50" borderRadius="16px" px={4} py={4}>
                      <Text color="gray.500">No upcoming calendar events were returned for this organization.</Text>
                    </Box>
                  )}
                </Stack>
              </SectionCard>
            </GridItem>

            <GridItem>
              <SectionCard
                title="Recent leads"
                subtitle="Newest pipeline entries."
                action={
                  <Button variant="ghost" color={brand} rightIcon={<Icon as={FiArrowUpRight as ElementType} />} onClick={() => navigate(`/organization/${org.id}/leads-dashboard`)}>
                    View all leads
                  </Button>
                }
              >
                <Stack spacing={3}>
                  {recentLeads.length > 0 ? (
                    recentLeads.map((lead) => (
                      <Box
                        key={lead.id}
                        bg="gray.50"
                        borderRadius="18px"
                        px={4}
                        py={4}
                        cursor="pointer"
                        transition="all 0.18s ease"
                        _hover={{ bg: 'white', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)' }}
                        onClick={() => navigate(`/organization/${org.id}/leads/${lead.id}`)}
                      >
                        <HStack justify="space-between" align="flex-start" mb={2}>
                          <Box pr={3}>
                            <Text fontWeight="800" color="gray.900">
                              {getLeadDisplayName(lead)}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {getLeadCompanyName(lead)}
                            </Text>
                          </Box>
                          <Badge borderRadius="full" px={2.5} py={1} bg={brandSoft} color={brand}>
                            {getStageLabel(lead.stage)}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between" flexWrap="wrap" spacing={3}>
                          <Text fontSize="sm" color="gray.500">
                            Created {formatCompactDate(lead.created_at)}
                          </Text>
                          <Badge colorScheme="purple" borderRadius="full" px={2.5} py={1}>
                            {lead.level_of_interest_display || getStageLabel(lead.level_of_interest)}
                          </Badge>
                        </HStack>
                      </Box>
                    ))
                  ) : (
                    <Box bg="gray.50" borderRadius="16px" px={4} py={4}>
                      <Text color="gray.500">Recent leads will appear here once the pipeline starts filling up.</Text>
                    </Box>
                  )}
                </Stack>
              </SectionCard>
            </GridItem>
          </Grid>
        </Stack>
      )}
    </Box>
  );
};

export default HomePrimaryDashboard;
